import { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useCustomerSummary } from "@api/intelligenceController";
import { markdownComponents } from "@areas/chatbot/utils";
import AiSparkleIcon from "../SmartNoteModal/AiSparkleIcon";

// Break the read into sections that each begin with an emoji, so they fade in one after another.
// Lines before the first emoji attach to the current section. (Mirrors SummaryCard.)
export function splitIntoSections(markdown) {
  const startsWithEmoji = (line) => /^\s*\p{Extended_Pictographic}/u.test(line);
  const sections = [];
  for (const line of markdown.split("\n")) {
    if (startsWithEmoji(line) || sections.length === 0) {
      sections.push(line);
    } else {
      sections[sections.length - 1] += `\n${line}`;
    }
  }
  return sections.filter((section) => section.trim() !== "");
}

// The "Ontik read" card on the Read tab: the emoji-led summary sections, one row each. Reports readiness up
// so the composer holds its rotating example placeholder (and its suggestions) until the read has landed.
export const SECTION_STAGGER_MS = 180;
// The suggestion panel's first row waits SUGGESTION_FIRST_ROW_DELAY_MS after mounting, so this lands it one
// section-beat after the last read section: the rows continue the read's wave rather than pausing after it.
export const SUGGESTION_FIRST_ROW_DELAY_MS = 80;
const REVEALED_AFTER_LAST_MS = SECTION_STAGGER_MS - SUGGESTION_FIRST_ROW_DELAY_MS;

export default function ReadBlock({ customerId, onReadyChange, onRevealed }) {
  const { data, isFetching, isError } = useCustomerSummary(customerId);
  const summary = data?.summary ?? "";

  const readReady = isError || !isFetching;
  useEffect(() => {
    onReadyChange?.(readReady, customerId);
  }, [readReady, customerId, onReadyChange]);

  // The sections to render, one emoji-led block each. Empty while loading and on error.
  const sections = readReady && !isError ? splitIntoSections(summary) : [];

  // Report once the read has landed — shortly after the last section starts revealing, or straight away
  // when there's nothing to animate (error/empty). The composer holds its suggestions until this. Not the
  // animation's end: its ease-out tail looks finished long before it is, which read as dead time.
  const revealedRef = useRef(false);
  useEffect(() => {
    revealedRef.current = false;
  }, [customerId]);
  const fireRevealed = () => {
    if (revealedRef.current) return;
    revealedRef.current = true;
    onRevealed?.(customerId);
  };
  // customerId is a dep so switching between two cached reads of the same length still re-fires.
  useEffect(() => {
    if (readReady && sections.length === 0) fireRevealed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readReady, sections.length, customerId]);
  useEffect(() => {
    if (!readReady || sections.length === 0) return undefined;
    const timer = setTimeout(fireRevealed, (sections.length - 1) * SECTION_STAGGER_MS + REVEALED_AFTER_LAST_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readReady, sections.length, customerId]);

  return (
    <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white">
      <div className="flex items-center gap-1.5 border-b border-zinc-100 px-4 py-3 text-xs font-medium uppercase tracking-wide text-zinc-500">
        <AiSparkleIcon className="size-4" />
        Ontik's read
      </div>

      {isError ? (
        <p className="px-4 py-6 text-center text-sm text-zinc-400">
          The read couldn't be loaded right now.
        </p>
      ) : isFetching ? (
        <div className="space-y-2.5 px-4 py-4">
          {["w-full", "w-11/12", "w-10/12", "w-full", "w-9/12", "w-11/12"].map((width, i) => (
            <div
              key={i}
              className={`h-3 ${width} animate-pulse rounded bg-zinc-200`}
              style={{ animationDelay: `${i * 120}ms` }}
            />
          ))}
        </div>
      ) : (
        <div key={summary} className="divide-y divide-zinc-100">
          {sections.map((section, i) => (
            <div
              key={i}
              className="animate-readIn px-4 py-3 text-sm leading-relaxed text-zinc-700 [&_strong]:font-semibold [&_strong]:text-zinc-900"
              style={{ animationDelay: `${i * SECTION_STAGGER_MS}ms` }}
            >
              <ReactMarkdown components={markdownComponents}>{section}</ReactMarkdown>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
